import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { I18nTranslator } from '../../../../i18n/translate';
import { Farm } from '../../../../models/farm.model';
import { Organisation } from '../../../../models/organisation.model';
import { Page } from '../../../../models/page.model';
import { ARGenConsoleService } from '../../../../services/agrorendement-genconsole.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ModalService } from '../../../../services/modal.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { toNull } from '../../../../utils/utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';
import { PaginationModule } from 'ngx-bootstrap/pagination';

@Component({
  standalone: true,
  selector: 'ar-genconsole-selectfarms-cmp',
  templateUrl: './genconsole.selectfarms.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, TranslatePipe, FormsModule, PaginationModule],
  providers: [ARGenConsoleService, OrganisationService],
})
export class ARGenConsoleSelectFarmsComponent implements OnInit, AfterViewInit {
  @Input()
  culture: string;

  error: any;
  @ViewChild('filter') filterElement: ElementRef;
  @ViewChild('termOrganisationFilter') termOrganisationFilter: ElementRef;
  @ViewChildren('farmCheckbox') farmCheckboxes: any;

  public rows: Array<any> = [];
  public organisationFilter: number = null;
  public page: number = 1;
  public itemsPerPage: number = 10;
  public maxSize: number = 5;
  public numPages: number = 1;
  public length: number = 0;
  public filter: string = '';
  public showRef: boolean;
  public OADPage: string = '';
  public organisations: Organisation[];

  @Input('farmIds')
  checkedFarmIds: Set<number>;

  @Input('farmIdMapping')
  farmIdsNames: Map<number, string>;

  constructor(
    private genconsoleService: ARGenConsoleService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private modal: ModalService,
    private organisationService: OrganisationService,
  ) {
    this.showRef =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
  }

  getFarmsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: any) {
    this.genconsoleService
      .getFarmsAdvanced(this.culture, pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe(
        (page: any) => this.handlePage(page),
        (e) => {
          this.handleError(e);
        },
      );
  }

  handlePage(page: Page<Farm>) {
    this.rows = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  backTo() {
    if (this.OADPage) {
      this.router.navigate(['/results/azote/' + this.OADPage + '']);
    } else {
      this.router.navigate(['/results']);
    }
  }
  handleError(e: any) {
    if (e.status == 400) {
      this.modal.alert(this.i18nTranslator.translate('ar.gc.sf.noParcelles')).then(() => this.backTo());
    } else {
      this.modal.alert(this.i18nTranslator.translate('ar.gc.sf.error.exploitations'));
    }
  }

  ngOnInit() {
    this.onChangeTable();
    if (this.showRef) {
      this.getOrganisations();
    }
    const str = this.router.url.split('/');
    if (str.length >= 4) {
      this.OADPage = str[3];
    }
  }

  ngAfterViewInit() {
    const observables = new Array<Observable<{}>>();
    const obs1 = fromEvent(this.filterElement.nativeElement, 'keyup').pipe(debounceTime(500), distinctUntilChanged());
    if (this.showRef) {
      const obs2 = fromEvent(this.termOrganisationFilter.nativeElement, 'change');
      observables.push(obs2);
    }
    observables.push(obs1);
    obs1.subscribe((term) => (this.filter = <string>this.filterElement.nativeElement.value));
    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });
  }

  clearFilters() {
    if (this.showRef) {
      this.termOrganisationFilter.nativeElement.value = null;
      this.organisationFilter = null;
    }
    this.filter = null;
    this.filterElement.nativeElement.value = null;
    this.updateFilters();
  }

  updateFilters() {
    // reset la page a la premiere et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du controle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    const filter = this.buildFilter();
    this.getFarmsPaginated(pageNumber, null, null, filter);
  }

  isFarmChecked(farm: Farm): boolean {
    return this.checkedFarmIds.has(farm.id);
  }

  buildFilter() {
    const filter = {
      filter: this.filter,
      organisation: toNull(this.organisationFilter),
    };
    return filter;
  }
  onCheckboxClicked(index: number) {
    const checkboxes = this.farmCheckboxes.toArray().map((x: any) => x.nativeElement);
    const farm = this.rows[index];
    const checkbox = checkboxes[index];
    if (checkbox.checked) {
      this.checkedFarmIds.add(farm.id);
      this.farmIdsNames.set(farm.id, farm.name);
    } else {
      this.checkedFarmIds.delete(farm.id);
      this.farmIdsNames.delete(farm.id);
    }
  }

  getOrganisations() {
    this.organisationService.getOrganisations().subscribe((orgs) => {
      this.organisations = orgs.sort((a, b) => a.name.localeCompare(b.name));
    });
  }
}
